/**
 * Getting Started guide arithmetic — pure, so the modal, the banner and the toast cannot disagree.
 *
 * Lives in utils (not services) so a page test that automocks a service never turns these into
 * `undefined` (CLAUDE.md §Mutation checks, session 319).
 *
 * The rules (TENANT_SETUP_SEQUENCE_GAPS W3/W8/W10/W12):
 * - A step is DONE when its data exists. Data wins over a skip.
 * - Otherwise it is SKIPPED when it is skippable and the owner skipped it — and a skipped step counts
 *   as done everywhere a count is shown.
 * - A step hidden by the production config (co-pack) is not counted at all.
 * - A plan-gated step on a lower plan is still listed (`locked`), so it can sell the upgrade.
 * - The CURRENT stage is the first stage with an open step; the NEXT step is that stage's first open
 *   step. Everything done or skipped ⇒ `allComplete`, and the banner goes away.
 */
import type { OnboardingProgress } from '@/services/onboarding';
import {
  ONBOARDING_STAGES,
  type OnboardingStageDef,
  type OnboardingStepDef,
  type OnboardingVisibilityKey,
} from '@/constants/onboardingSteps';
import { canAccessPlanFeature, type PlanId } from '@/constants/planLimits';

export type OnboardingStepStatus = 'done' | 'skipped' | 'open';

export interface OnboardingStepState {
  def: OnboardingStepDef;
  status: OnboardingStepStatus;
  /** On a lower plan: shows GROWTH and opens the upgrade prompt instead of the page. */
  locked: boolean;
  route: string;
}

export interface OnboardingStageState {
  def: OnboardingStageDef;
  steps: OnboardingStepState[];
  /** done + skipped */
  finished: number;
  total: number;
  complete: boolean;
}

export interface OnboardingState {
  stages: OnboardingStageState[];
  currentStage: OnboardingStageState | null;
  nextStep: OnboardingStepState | null;
  finished: number;
  total: number;
  allComplete: boolean;
}

export type OnboardingVisibility = Partial<Record<OnboardingVisibilityKey, boolean>>;

export function isStepVisible(def: OnboardingStepDef, visibility: OnboardingVisibility): boolean {
  return !def.visibilityKey || visibility[def.visibilityKey] === true;
}

export function resolveStepRoute(
  def: OnboardingStepDef,
  progress: Pick<OnboardingProgress, 'currentUserStaffId'> | null
): string {
  return typeof def.route === 'function' ? def.route(progress ?? {}) : def.route;
}

export function stepStatus(
  def: OnboardingStepDef,
  progress: OnboardingProgress | null,
  skipped: ReadonlySet<string>
): OnboardingStepStatus {
  if (progress?.[def.key] === true) return 'done';
  if (def.skipLabel && skipped.has(def.key)) return 'skipped';
  return 'open';
}

/**
 * Build the whole guide state. `skipped` is passed separately from `progress` so the context can
 * apply a skip optimistically before the server answers.
 */
export function buildOnboardingState(
  progress: OnboardingProgress | null,
  skipped: ReadonlySet<string>,
  visibility: OnboardingVisibility,
  plan: PlanId | null | undefined,
  stages: OnboardingStageDef[] = ONBOARDING_STAGES
): OnboardingState {
  const currentPlan: PlanId = plan || 'starter';

  const built: OnboardingStageState[] = [];
  for (const def of stages) {
    const steps = def.steps
      .filter((step) => isStepVisible(step, visibility))
      .map((step) => ({
        def: step,
        status: stepStatus(step, progress, skipped),
        locked: Boolean(step.planFeature) && !canAccessPlanFeature(currentPlan, step.planFeature as string),
        route: resolveStepRoute(step, progress),
      }));
    if (steps.length === 0) continue; // a stage with nothing visible is not shown or counted
    const finished = steps.filter((s) => s.status !== 'open').length;
    built.push({
      def,
      steps,
      finished,
      total: steps.length,
      complete: finished === steps.length,
    });
  }

  const currentStage = built.find((stage) => !stage.complete) ?? null;
  const nextStep = currentStage?.steps.find((step) => step.status === 'open') ?? null;
  const finished = built.reduce((sum, stage) => sum + stage.finished, 0);
  const total = built.reduce((sum, stage) => sum + stage.total, 0);

  return {
    stages: built,
    currentStage,
    nextStep,
    finished,
    total,
    allComplete: total > 0 && finished === total,
  };
}

/**
 * The step whose DATA just appeared, for the step-complete toast. Data only: a skip never says
 * "Variants added". First match in guide order, among visible steps.
 */
export function newlyCompletedStep(
  prev: OnboardingProgress | null,
  fresh: OnboardingProgress,
  visibility: OnboardingVisibility,
  stages: OnboardingStageDef[] = ONBOARDING_STAGES
): OnboardingStepDef | null {
  if (!prev) return null;
  for (const stage of stages) {
    for (const step of stage.steps) {
      if (!isStepVisible(step, visibility)) continue;
      if (prev[step.key] !== true && fresh[step.key] === true) return step;
    }
  }
  return null;
}

/** Only steps that declare a skip label may be skipped — the guard behind the button. */
export function isSkippable(stepKey: string, stages: OnboardingStageDef[] = ONBOARDING_STAGES): boolean {
  return stages.some((stage) => stage.steps.some((step) => step.key === stepKey && Boolean(step.skipLabel)));
}
